// Mirrors BE apps/be/src/services/caseEvents/caseEventReads.ts (`CasesEventsResult`, `CaseEventsResult`), by hand.
import * as v from 'valibot';

import { isNumericIdString, logValidationErrorsToSentry } from '#shared';

import { domainEventFrameSchema } from '#entities/DomainEvent';
import type { DomainEventFrame } from '#entities/DomainEvent';

import { EValidationIssueSeverity, issueSeverity } from '#shared/api';
import type { DataWithValidation, ValidationIssue } from '#shared/api';

import { APPROVAL_SLOT_KEYS } from '../../model/types';

import type {
  ApprovalSlot,
  CaseAirLeg,
  CaseApprovals,
  CaseTimelineCaseData,
  CaseTimelineRawEvent,
  CaseTimelineResult,
  CasesTimeline,
} from '../../model/types';

const seqString = v.pipe(
  v.string(),
  v.check(isNumericIdString, 'seq must be numeric'),
);

const parseableTimestamp = v.pipe(
  v.string(),
  v.check(
    value => !Number.isNaN(Date.parse(value)),
    'must be a parseable timestamp',
  ),
);

const warningNullableString = (field: string) =>
  v.nullable(v.string(`@WARNING: ${field} is not a string`));

const warningTimestamp = (field: string) =>
  v.pipe(
    v.string(`@WARNING: ${field} is not a string`),
    v.check(
      value => !Number.isNaN(Date.parse(value)),
      `@WARNING: ${field} is not a parseable timestamp`,
    ),
  );

const warningNullableTimestamp = (field: string) =>
  v.nullable(warningTimestamp(field));

export const caseTimelineFrameSchema = v.object({
  ...domainEventFrameSchema.entries,
  id: v.pipe(
    v.string(),
    v.regex(/^\d+(:\d+)?$/, 'frame id must be <seq> or <seq>:<n>'),
  ),
});

// duplicate-ok: the runtime check of `CaseTimelineMediaFile`, graded WARNING field by field
const caseTimelineMediaFileSchema = v.object(
  {
    path: v.string('@WARNING: media file path is not a string'),
    type: v.picklist(['photo', 'video'], '@WARNING: unknown media file type'),
    url: v.optional(v.string('@WARNING: media file url is not a string')),
    posterUrl: v.optional(
      v.string('@WARNING: media file posterUrl is not a string'),
    ),
  },
  '@WARNING: media file is not an object',
);

export const caseTimelineRawEventSchema = v.object({
  seq: seqString,
  type: v.pipe(v.string(), v.nonEmpty()),
  eventId: v.string(),
  occurredAt: parseableTimestamp,
  observedAt: parseableTimestamp,
  at: parseableTimestamp,
  payload: v.unknown(),
  previous: v.optional(
    v.record(v.string(), v.unknown(), '@WARNING: previous is not an object'),
  ),
  media: v.optional(
    v.array(caseTimelineMediaFileSchema, '@WARNING: media is not an array'),
  ),
});

export const caseAirLegSchema = v.object({
  legId: v.pipe(v.string(), v.nonEmpty()),
  maoTripId: v.number(),
  maoLegId: v.number(),
  departureIcao: warningNullableString('departureIcao'),
  arrivalIcao: warningNullableString('arrivalIcao'),
  plannedOffBlocksUtc: warningNullableString('plannedOffBlocksUtc'),
  plannedOnBlocksUtc: warningNullableString('plannedOnBlocksUtc'),
  actualTakeOffUtc: warningNullableTimestamp('actualTakeOffUtc'),
  actualLandingUtc: warningNullableTimestamp('actualLandingUtc'),
});

const approvalSlotSchema = v.object(
  {
    key: v.picklist(APPROVAL_SLOT_KEYS, '@WARNING: unknown approval slot key'),
    state: v.picklist(
      ['absent', 'outstanding', 'approved', 'declined'],
      '@WARNING: unknown approval slot state',
    ),
    requestedAt: warningNullableTimestamp('requestedAt'),
    answeredAt: warningNullableTimestamp('answeredAt'),
  },
  '@WARNING: approval slot is not an object',
);

const organAcceptanceRoundSchema = v.object(
  {
    requestedAt: warningTimestamp('requestedAt'),
    answeredAt: warningNullableTimestamp('answeredAt'),
    outcome: v.nullable(
      v.picklist(
        ['accepted', 'declined'],
        '@WARNING: unknown organ acceptance outcome',
      ),
    ),
  },
  '@WARNING: organ acceptance round is not an object',
);

const hasEverySlotInOrder = (slots: ApprovalSlot[]) =>
  slots.length === APPROVAL_SLOT_KEYS.length &&
  slots.every((slot, index) => slot.key === APPROVAL_SLOT_KEYS[index]);

/** A missing `approvals` defaults to null, so it grades as a WARNING rather than a missing-key ERROR. */
const caseApprovalsSchema = v.pipe(
  v.optional(v.unknown(), null),
  v.object(
    {
      slots: v.pipe(
        v.array(approvalSlotSchema, '@WARNING: approval slots is not an array'),
        v.check(
          hasEverySlotInOrder,
          '@WARNING: approval slots are not the five keys in order',
        ),
      ),
      organRounds: v.array(
        organAcceptanceRoundSchema,
        '@WARNING: organ acceptance rounds is not an array',
      ),
    },
    '@WARNING: approvals is missing or not an object',
  ),
);

const absentApprovals = (): CaseApprovals => ({
  slots: APPROVAL_SLOT_KEYS.map(key => ({
    key,
    state: 'absent',
    requestedAt: null,
    answeredAt: null,
  })),
  organRounds: [],
});

export const caseTimelineCaseSchema = v.object({
  caseId: v.pipe(v.string(), v.nonEmpty()),
  caseLabel: v.optional(warningNullableString('caseLabel')),
  frames: v.array(v.unknown()),
  events: v.array(v.unknown()),
  airLegs: v.array(v.unknown()),
  approvals: caseApprovalsSchema,
});

const AIR_LEG_FALLBACKS: Partial<Record<keyof CaseAirLeg, null>> = {
  departureIcao: null,
  arrivalIcao: null,
  plannedOffBlocksUtc: null,
  plannedOnBlocksUtc: null,
  actualTakeOffUtc: null,
  actualLandingUtc: null,
};

type Path = (string | number)[];

const toIssue = (
  prefix: Path,
  issue: v.BaseIssue<unknown>,
): ValidationIssue => {
  const path = [
    ...prefix,
    ...(issue.path?.map(item => item.key as string | number) ?? []),
  ];
  const pathKey = path.join('.') || 'root';
  return {
    path,
    pathKey,
    severity: issueSeverity(issue),
    message: issue.message.replace(/^@(WARNING|ERROR):/i, '').trim(),
    key: pathKey,
  };
};

const isError = (issue: ValidationIssue) =>
  issue.severity === EValidationIssueSeverity.ERROR;

interface Graded<T> {
  value: T | null;
  issues: ValidationIssue[];
}

/** Drops the item on an ERROR issue; on WARNING-only issues keeps it with each failed field set to its fallback. */
const gradeItem = <T>(
  schema: v.GenericSchema<unknown, T>,
  raw: unknown,
  prefix: Path,
  fallbacks: Partial<Record<string, unknown>> = {},
): Graded<T> => {
  const result = v.safeParse(schema, raw);
  if (result.success) {
    return { value: result.output, issues: [] };
  }
  const issues = result.issues.map(issue => toIssue(prefix, issue));
  if (issues.some(isError)) {
    return { value: null, issues };
  }
  const failed = new Set(result.issues.map(issue => issue.path?.[0]?.key));
  const kept = Object.fromEntries(
    Object.entries(result.output as Record<string, unknown>)
      .map(([key, value]) => [key, failed.has(key) ? fallbacks[key] : value])
      .filter(([, value]) => value !== undefined),
  );
  return { value: kept as T, issues };
};

const gradeList = <T>(
  schema: v.GenericSchema<unknown, T>,
  items: readonly unknown[],
  prefix: Path,
  fallbacks?: Partial<Record<string, unknown>>,
): { kept: T[]; issues: ValidationIssue[] } => {
  const kept: T[] = [];
  const issues: ValidationIssue[] = [];
  items.forEach((item, index) => {
    const graded = gradeItem(schema, item, [...prefix, index], fallbacks);
    issues.push(...graded.issues);
    if (graded.value !== null) {
      kept.push(graded.value);
    }
  });
  return { kept, issues };
};

interface GradedCase {
  value: CaseTimelineCaseData | null;
  issues: ValidationIssue[];
  rawCount: number;
  droppedCount: number;
}

const gradeCase = (raw: unknown, prefix: Path): GradedCase => {
  const shell = gradeItem(caseTimelineCaseSchema, raw, prefix, {
    caseLabel: null,
    approvals: absentApprovals(),
  });
  if (shell.value === null) {
    return { value: null, issues: shell.issues, rawCount: 1, droppedCount: 1 };
  }
  const frames = gradeList(
    caseTimelineFrameSchema as v.GenericSchema<unknown, DomainEventFrame>,
    shell.value.frames,
    [...prefix, 'frames'],
  );
  const events = gradeList(
    caseTimelineRawEventSchema as v.GenericSchema<
      unknown,
      CaseTimelineRawEvent
    >,
    shell.value.events,
    [...prefix, 'events'],
    { previous: undefined, media: undefined },
  );
  const airLegs = gradeList(
    caseAirLegSchema as v.GenericSchema<unknown, CaseAirLeg>,
    shell.value.airLegs,
    [...prefix, 'airLegs'],
    AIR_LEG_FALLBACKS,
  );
  const items =
    shell.value.frames.length +
    shell.value.events.length +
    shell.value.airLegs.length;
  const kept = frames.kept.length + events.kept.length + airLegs.kept.length;
  return {
    value: {
      caseId: shell.value.caseId,
      caseLabel: shell.value.caseLabel ?? null,
      frames: frames.kept,
      events: events.kept,
      airLegs: airLegs.kept,
      approvals: shell.value.approvals,
    },
    issues: [
      ...shell.issues,
      ...frames.issues,
      ...events.issues,
      ...airLegs.issues,
    ],
    rawCount: 1 + items,
    droppedCount: items - kept,
  };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const withValidation = <T>(
  endpoint: string,
  data: T,
  issues: ValidationIssue[],
  rawCount: number,
  droppedCount: number,
): DataWithValidation<T> => {
  const errorIssues = issues.filter(isError);
  if (errorIssues.length > 0) {
    logValidationErrorsToSentry(endpoint, errorIssues, rawCount, droppedCount);
  }
  return {
    data,
    generatedAt: null,
    validation: { isValid: issues.length === 0, issues },
  };
};

const envelopeIssue = (message: string): ValidationIssue => ({
  path: [],
  pathKey: 'root',
  severity: EValidationIssueSeverity.ERROR,
  message,
  key: 'root',
});

const latestSeqOf = (
  value: unknown,
): { latestSeq: string | null; issues: ValidationIssue[] } => {
  if (value === null || value === undefined) {
    return { latestSeq: null, issues: [] };
  }
  return typeof value === 'string' && isNumericIdString(value)
    ? { latestSeq: value, issues: [] }
    : {
        latestSeq: null,
        issues: [envelopeIssue('latestSeq is not a numeric id string')],
      };
};

export const parseCasesTimeline = (
  raw: unknown,
): DataWithValidation<CasesTimeline> => {
  if (!isObject(raw) || !Array.isArray(raw.cases)) {
    return withValidation(
      'casesTimeline',
      { latestSeq: null, cases: [] },
      [envelopeIssue('cases timeline response has no cases array')],
      1,
      1,
    );
  }
  const issues: ValidationIssue[] = [];
  const cases: CaseTimelineCaseData[] = [];
  let rawCount = 0;
  let droppedCount = 0;
  raw.cases.forEach((item, index) => {
    const graded = gradeCase(item, ['cases', index]);
    issues.push(...graded.issues);
    rawCount += graded.rawCount;
    droppedCount += graded.droppedCount;
    if (graded.value) {
      cases.push(graded.value);
    }
  });
  const { latestSeq, issues: seqIssues } = latestSeqOf(raw.latestSeq);
  return withValidation(
    'casesTimeline',
    { latestSeq, cases },
    [...seqIssues, ...issues],
    rawCount,
    droppedCount,
  );
};

export const parseCaseTimeline = (
  raw: unknown,
): DataWithValidation<CaseTimelineResult> => {
  const graded = gradeCase(raw, []);
  const { latestSeq, issues: seqIssues } = latestSeqOf(
    isObject(raw) ? raw.latestSeq : null,
  );
  return withValidation(
    'caseTimeline',
    { latestSeq, case: graded.value },
    [...seqIssues, ...graded.issues],
    graded.rawCount,
    graded.droppedCount,
  );
};
